import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Fields — §4. Label in mono above, help or error below; never both.
 *
 * React Aria's `TextField` / `Select` own the label association, the `aria-describedby`
 * wiring, the invalid state and the error announcement. What stays ours is the rule the
 * design states: **help or error, never both** — an invalid field replaces its help rather
 * than stacking a second line under it.
 *
 * A field's label carries `.lbl` rather than relying on `.field > label`: inside a `Select`
 * the trigger is a button, so React Aria renders the label as a `<span>` — correctly, since
 * a `<label>` cannot label a button — and an element selector would quietly stop matching.
 *
 * `SelectField` is a real RAC `Select`: a button, a popover and a listbox, so typeahead,
 * arrow keys, Escape and the collision-aware placement are RAC's, and the trigger is still
 * `.select` with the sheet's CSS-drawn chevron. It keeps the `<option>` children the app
 * already writes, read once into RAC items.
 */
import { Children, isValidElement } from 'react';
import { Button as AriaButton, FieldError, Input, Label, ListBox, ListBoxItem, Popover, Select, SelectValue, Text, TextArea, TextField, } from 'react-aria-components';
/** Help and error, under the control. Never both — the design's rule, not RAC's. */
function Below({ help, error }) {
    const invalid = error !== undefined && error !== '';
    return (_jsxs(_Fragment, { children: [help !== undefined && !invalid && (_jsx(Text, { slot: "description", className: "help", children: help })), _jsx(FieldError, { className: "err", children: error })] }));
}
export function Field({ label, labelHidden = false, help, error, placeholder, ...rest }) {
    const invalid = error !== undefined && error !== '';
    return (_jsxs(TextField, { ...rest, className: "field", isInvalid: invalid, children: [_jsx(Label, { className: labelHidden ? 'vh' : 'lbl', children: label }), _jsx(Input, { className: "input", placeholder: placeholder }), _jsx(Below, { help: help, error: error })] }));
}
export function TextAreaField({ label, labelHidden = false, help, error, placeholder, rows, ...rest }) {
    const invalid = error !== undefined && error !== '';
    return (_jsxs(TextField, { ...rest, className: "field", isInvalid: invalid, children: [_jsx(Label, { className: labelHidden ? 'vh' : 'lbl', children: label }), _jsx(TextArea, { className: "textarea", placeholder: placeholder, rows: rows }), _jsx(Below, { help: help, error: error })] }));
}
/**
 * An inline field — a control that *is* the text it edits (`studio/09`: the story page's
 * title). No chrome at rest and the `--focus` underline on focus come from `.inline-field`,
 * the one rule the line's textarea also wears; what the text *is* stays on the classes
 * passed in. Commit, revert and Enter are the caller's, through RAC's own events.
 */
export function InlineField({ label, className, inputRef, ...rest }) {
    return (_jsx(TextField, { ...rest, "aria-label": label, children: _jsx(Input, { ref: inputRef, className: `${className} inline-field` }) }));
}
function optionsOf(children) {
    return Children.toArray(children).flatMap((child) => {
        if (!isValidElement(child))
            return [];
        const { value, children: label, disabled } = child.props;
        return [
            {
                key: value === undefined ? String(label) : String(value),
                label,
                disabled: disabled === true,
            },
        ];
    });
}
export function SelectField({ label, labelHidden = false, help, error, children, value, defaultValue, onChange, compact = false, ...rest }) {
    const invalid = error !== undefined && error !== '';
    const options = optionsOf(children);
    return (_jsxs(Select, { ...rest, className: compact ? 'field field-inline' : 'field', isInvalid: invalid, ...(value === undefined ? {} : { selectedKey: value }), ...(defaultValue === undefined ? {} : { defaultSelectedKey: defaultValue }), onSelectionChange: (key) => onChange?.(String(key)), children: [_jsx(Label, { className: labelHidden ? 'vh' : 'lbl', children: label }), _jsx(AriaButton, { className: compact ? 'select select-sm' : 'select', children: _jsx(SelectValue, { className: "select-value" }) }), _jsx(Below, { help: help, error: error }), _jsx(Popover, { className: "pop", children: _jsx(ListBox, { className: "pop-list", children: options.map((option) => (_jsx(ListBoxItem, { id: option.key, isDisabled: option.disabled, className: "pop-item", children: option.label }, option.key))) }) })] }));
}
//# sourceMappingURL=Field.js.map